import { useState, useEffect, useCallback } from 'react';
import { Auth, API, graphqlOperation } from 'aws-amplify';
import {
  getLessonProgress,
  getModuleProgress,
  listLessonProgresses,
  listModuleProgresses,
  getUserProgress,
} from './graphql/queries';
import {
  createLessonProgress,
  createModuleProgress,
  updateLessonProgress,
  updateModuleProgress,
  createUserProgress,
} from './graphql/mutations';

const useProgress = (courseId, courseData) => {
  const [lessonProgress, setLessonProgress] = useState({});
  const [moduleProgress, setModuleProgress] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const updateLessonProgressStatus = async (
    lessonId,
    moduleId,
    completed,
    progressPercentage
  ) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const userSub = user.attributes.sub;
      //console.log('Starting progress update for lesson:', lessonId);

      // Update local state immediately for better UX
      setLessonProgress(prev => ({
        ...prev,
        [lessonId]: {
          ...prev[lessonId],
          completed,
          progressPercentage,
        },
      }));

      const userProgressId = `${userSub}-${courseId}`;
      
      // Create or update LessonProgress first
      const lessonProgressId = `${userSub}-${lessonId}`;
      const lessonProgressInput = {
        id: lessonProgressId,
        userID: userSub,
        lessonID: lessonId,
        moduleID: moduleId,
        completed: completed,
        progressPercentage: completed ? 100 : 0,
        lastViewedAt: new Date().toISOString()
      };

      // If updating existing lesson progress, include the version
      if (lessonProgress[lessonId]?._version) {
        lessonProgressInput._version = lessonProgress[lessonId]._version;
        //console.log('Updating existing lesson progress with version:', lessonProgressInput._version);
        const updateResponse = await API.graphql(
          graphqlOperation(updateLessonProgress, { input: lessonProgressInput })
        );
        //console.log('Lesson progress update response:', updateResponse);
      } else {
        //console.log('Creating new lesson progress');
        const createResponse = await API.graphql(
          graphqlOperation(createLessonProgress, { input: lessonProgressInput })
        );
        //console.log('Lesson progress create response:', createResponse);
      }

      // Fetch fresh data to confirm the update
      await fetchProgress();
      //console.log('Progress update completed successfully');

      return true; // Return success status

    } catch (error) {
      console.error('Error updating progress:', error);
      console.error('Error details:', error.errors || error);
      await fetchProgress();
      return false; // Return failure status
    }
  };

  const fetchProgress = useCallback(async () => {
    //console.log('Fetching progress for courseId:', courseId);
    setIsLoading(true);
    try {
      const user = await Auth.currentAuthenticatedUser();
      const userSub = user.attributes.sub;
      
      // First, get UserProgress
      const userProgressId = `${userSub}-${courseId}`;
      const userProgressResponse = await API.graphql(
        graphqlOperation(getUserProgress, { id: userProgressId })
      );
      //console.log('UserProgress Response:', userProgressResponse);

      // Get ModuleProgress records
      const moduleProgressResponse = await API.graphql(
        graphqlOperation(listModuleProgresses, {
          filter: {
            userProgressID: { eq: userProgressId },
            _deleted: { ne: true }
          }
        })
      );
      //console.log('ModuleProgress Response:', moduleProgressResponse);

      const moduleProgressItems = moduleProgressResponse.data.listModuleProgresses.items;
      const newModuleProgressMap = {};
      moduleProgressItems.forEach(item => {
        if (item?.moduleID) {
          newModuleProgressMap[item.moduleID] = {
            id: item.id,
            progressPercentage: item.progressPercentage,
            completed: item.completed,
            _version: item._version
          };
        }
      });

      // Get LessonProgress records with pagination
      let allLessonProgressItems = [];
      let nextToken = null;

      do {
        const lessonProgressResponse = await API.graphql(
          graphqlOperation(listLessonProgresses, {
            filter: {
              userID: { eq: userSub },
              _deleted: { ne: true }
            },
            limit: 1000,
            nextToken
          })
        );
        //console.log('Lesson Progress Response:', lessonProgressResponse);
        
        const items = lessonProgressResponse.data.listLessonProgresses.items;
        allLessonProgressItems = [...allLessonProgressItems, ...items];
        nextToken = lessonProgressResponse.data.listLessonProgresses.nextToken;
      } while (nextToken);

      const newLessonProgressMap = {};
      allLessonProgressItems.forEach(item => {
        if (item?.lessonID) {
          newLessonProgressMap[item.lessonID] = {
            completed: item.completed,
            progressPercentage: item.progressPercentage,
            id: item.id,
            _version: item._version,
            lastViewedAt: item.lastViewedAt
          };
        }
      });

      setModuleProgress(newModuleProgressMap);
      setLessonProgress(newLessonProgressMap);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching progress:', error);
      setIsLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (courseId) {
      fetchProgress();
    }
  }, [courseId, fetchProgress]);

  return {
    lessonProgress,
    moduleProgress,
    isLoading,
    fetchProgress,
    updateLessonProgressStatus
  };
};

export default useProgress;